<script setup lang="ts">
/**
 * Table cell with a two-line text layout.
 *
 * `title` is rendered on the primary line in the normal text color, at
 * `--text-tufte-lg` — both current callers pass a business service type's
 * own name here, which gets this size consistently everywhere the type
 * name appears (table, cards, forms). `subtitle` is rendered below it,
 * muted and smaller (matching the secondary line of `StartDateCell` /
 * `DateTimeCell`). Either field may be `null` or blank —
 * the component only renders the line(s) that have content, collapsing to a
 * single-line cell when `subtitle` is absent. Shows `emptyPlaceholder` when
 * both are blank.
 *
 * Used in `ServicesTab` (Service Type column: description over category+duration)
 * and `ServiceTypesPane` (Name column: description over category+duration).
 */
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    /** Upper line, rendered in normal text color. */
    title?: string | null
    /** Lower line, rendered muted and smaller (mirrors StartDateCell's secondary line). */
    subtitle?: string | null
    /** Placeholder shown when both `title` and `subtitle` are missing/blank; rendered muted. */
    emptyPlaceholder?: string
    /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
    maxWidth?: string
  }>(),
  { emptyPlaceholder: '' },
)

function nonEmpty(value: string | null | undefined): string | null {
  if (value == null) return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

const titleText = computed(() => nonEmpty(props.title))
const subtitleText = computed(() => nonEmpty(props.subtitle))
const isEmpty = computed(() => titleText.value == null && subtitleText.value == null)
</script>

<template>
  <td
    class="font-tufte text-tufte-text"
    :style="props.maxWidth ? { maxWidth: props.maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <div v-if="!isEmpty" class="flex flex-col gap-0.5">
      <span v-if="titleText" style="font-size: var(--text-tufte-lg)">{{ titleText }}</span>
      <span v-if="subtitleText" class="text-tufte-muted" style="font-size: var(--text-tufte-xs)">
        {{ subtitleText }}
      </span>
    </div>
    <span v-else-if="emptyPlaceholder !== ''" class="text-tufte-muted">{{ emptyPlaceholder }}</span>
  </td>
</template>
